import { useState } from 'react';
import { useParams } from 'react-router';
import { Trans, useLingui } from '@lingui/react/macro';

import { GenericError } from '@/components/GenericError/GenericError';
import { useNavigate } from '@/components/Link/Link';
import Box from '@/design_system/Box';
import Button from '@/design_system/Button';
import ConfirmationDialog from '@/design_system/Dialog/ConfirmationDialog';
import { PaginationFooter } from '@/design_system/Pagination/Pagination';
import Stack from '@/design_system/Stack';
import { Body, Cell, Column, Header, Row, Table } from '@/design_system/Table/Table';
import { useShowToast } from '@/design_system/Toast';
import EditableTitle from '@/design_system/Typography/EditableTitle';
import Paragraph from '@/design_system/Typography/Paragraph';
import Title from '@/design_system/Typography/Title';
import { formatAddress } from '@/models/address';
import { useDeleteEntityGroup, useEntityGroup, useUpdateEntityGroup } from '@/models/entityGroup';
import { ErrorState } from '@/routes/SettingsBeta/components/ErrorState';
import { SettingsBreadcrumbParent } from '@/routes/SettingsBeta/components/Layout/Layout';
import { Loading } from '@/routes/SettingsBeta/components/Loading';
import { NotFoundError } from '@/utils/error/NotFoundError';

import { AddEntityToGroup } from './components/AddEntityToGroup';
import { RemoveEntityFromGroupButton } from './components/RemoveEntityFromGroupButton';

const ENTITIES_PER_PAGE = 20;

export const Group = () => {
  const { id } = useParams();
  const { t } = useLingui();
  const showToast = useShowToast();
  const [page, setPage] = useState(1);
  const { mutateAsync: updateEntityGroup, isPending: isUpdateEntityGroupPending } =
    useUpdateEntityGroup();

  const {
    data: { entityGroup } = {},
    isLoading: isEntityGroupLoading,
    isError: isEntityGroupError,
    error,
  } = useEntityGroup(id);

  if (isEntityGroupLoading) {
    return <Loading />;
  }

  if (isEntityGroupError || !entityGroup) {
    let errorMessage = <GenericError />;

    if (error instanceof NotFoundError) {
      errorMessage = (
        <Paragraph>
          <Trans id="settings.organization.entities.groups.detail.not-found">
            Unknown group or you do not have access to it.
          </Trans>
        </Paragraph>
      );
    }

    return <ErrorState errorMessage={errorMessage} />;
  }

  const handleSaveGroupName = async (name: string) => {
    if (!id) {
      return;
    }

    try {
      await updateEntityGroup({
        entityGroupId: id,
        body: { name },
      });
    } catch {
      showToast({
        text: (
          <Trans id="settings.organization.entities.groups.detail.rename.error">
            Could not rename the group. Please try again.
          </Trans>
        ),
        type: 'error',
      });
    }
  };

  const start = (page - 1) * ENTITIES_PER_PAGE;
  const paginatedEntities = entityGroup.entities.slice(start, start + ENTITIES_PER_PAGE);

  return (
    <main>
      <Stack
        row
        className="settings-section__record-header"
        gap="0.5rem"
        alignItems="center"
        flexWrap="wrap"
      >
        <SettingsBreadcrumbParent
          to="/settings_beta/network/groups"
          label={
            <Trans id="settings.organization.entities.groups.detail.back-to-groups">Groups</Trans>
          }
        />

        <EditableTitle
          value={entityGroup.name}
          onSave={handleSaveGroupName}
          isSaving={isUpdateEntityGroupPending}
          titleProps={{ as: 'h1', size: '200', weight: 'medium' }}
          editAriaLabel={t({
            id: 'settings.organization.entities.groups.detail.editable-title.edit',
            message: 'Edit group name',
          })}
          saveAriaLabel={t({
            id: 'settings.organization.entities.groups.detail.editable-title.save',
            message: 'Save group name',
          })}
          cancelAriaLabel={t({
            id: 'settings.organization.entities.groups.detail.editable-title.cancel',
            message: 'Cancel renaming',
          })}
          inputAriaLabel={t({
            id: 'settings.organization.entities.groups.detail.editable-title.input',
            message: 'Group name',
          })}
        />
      </Stack>

      <Stack
        row
        justifyContent="space-between"
        alignItems="center"
        gap="1rem"
        style={{ flexWrap: 'wrap' }}
      >
        <Title as="h2" size="300" weight="medium">
          <Trans id="settings.organization.entities.groups.detail.list-of-entities">
            List of entities
          </Trans>
        </Title>
        <AddEntityToGroup entityGroupId={entityGroup.id} entities={entityGroup.entities} />
      </Stack>

      <Table
        columnWidths={['1fr', '1fr', 'minmax(auto, 80px)']}
        bordered
        ariaLabel={t({
          id: 'settings.organization.entities.groups.detail.table.aria_label',
          message: 'Entities in this group',
        })}
      >
        <Header>
          <Row>
            <Column>
              <Trans id="settings.organization.entities.groups.detail.table.header.name">
                Name
              </Trans>
            </Column>
            <Column>
              <Trans id="settings.organization.entities.groups.detail.table.header.address">
                Address
              </Trans>
            </Column>
            <Column
              ariaLabel={t({
                id: 'settings.organization.entities.groups.detail.table.header.actions',
                message: 'Actions',
              })}
            />
          </Row>
        </Header>
        <Body>
          {entityGroup.entities.length === 0 && (
            <Row>
              <Cell isWholeRow>
                <Paragraph>
                  <Trans id="settings.organization.entities.groups.detail.no-entities">
                    This group has no entities yet.
                  </Trans>
                </Paragraph>
              </Cell>
            </Row>
          )}
          {paginatedEntities.map((entity) => (
            <Row key={entity.id} to={`/settings_beta/network/entities/${entity.id}`}>
              <Cell isLink>
                <Paragraph weight="medium">{entity.name}</Paragraph>
              </Cell>
              <Cell>
                <Paragraph size="200">
                  {entity.address ? formatAddress(entity.address) : '-'}
                </Paragraph>
              </Cell>
              <Cell justifyContent="flex-end">
                <RemoveEntityFromGroupButton
                  entity={{ id: entity.id, name: entity.name }}
                  entityGroupId={entityGroup.id}
                  allEntities={entityGroup.entities}
                />
              </Cell>
            </Row>
          ))}
        </Body>
        <PaginationFooter
          count={entityGroup.entities.length}
          itemsPerPage={ENTITIES_PER_PAGE}
          page={page}
          onPageChange={setPage}
        />
      </Table>
      <Title as="h2" size="300" weight="medium">
        <Trans id="settings.organization.entities.groups.detail.settings.title">Settings</Trans>
      </Title>
      <Box style={{ flex: 'unset' }}>
        <Stack row gap="1rem" alignItems="center" justifyContent="space-between">
          <Stack flexWrap="wrap">
            <Paragraph weight="bold">
              <Trans id="settings.organization.entities.groups.detail.settings.delete.title">
                Delete this group
              </Trans>
            </Paragraph>
            <Paragraph size="200" color="secondary">
              <Trans id="settings.organization.entities.groups.detail.settings.delete.cannot-be-undone">
                This action cannot be undone.
              </Trans>
            </Paragraph>
          </Stack>
          <DeleteGroup />
        </Stack>
      </Box>
    </main>
  );
};

const DeleteGroup = () => {
  const { id } = useParams();

  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const { mutateAsync: deleteEntityGroup, isPending: isDeleteEntityGroupPending } =
    useDeleteEntityGroup();
  const navigate = useNavigate();

  return (
    <>
      <Button
        variant="secondary-danger"
        size="small"
        isLoading={isDeleteEntityGroupPending}
        onPress={() => setShowDeleteConfirmation(true)}
      >
        <Trans id="settings.organization.entities.groups.detail.delete.button">Delete group</Trans>
      </Button>
      <ConfirmationDialog
        showDialog={showDeleteConfirmation}
        setShowDialog={setShowDeleteConfirmation}
        title={
          <Trans id="settings.organization.entities.groups.detail.delete.confirmation.title">
            Are you sure you want to delete this group?
          </Trans>
        }
        confirm={
          <Trans id="settings.organization.entities.groups.detail.delete.confirmation.confirm">
            Delete
          </Trans>
        }
        onConfirm={() => {
          deleteEntityGroup(id!).then(() => {
            navigate('/settings_beta/network/groups');
          });
        }}
        maxWidth="35rem"
      >
        <Stack gap="0.5rem">
          <Paragraph>
            <Trans id="settings.organization.entities.groups.detail.delete.confirmation.description.effects">
              Deleting this group will have the following effects:
            </Trans>
          </Paragraph>
          <ul>
            <li>
              <Trans id="settings.organization.entities.groups.detail.delete.confirmation.description.entity-agreements">
                Entities in this group will no longer benefit from this group's agreements.
              </Trans>
            </li>
            <li>
              <Trans id="settings.organization.entities.groups.detail.delete.confirmation.description.user-roles">
                Users with roles on this group will no longer have these roles.
              </Trans>
            </li>
          </ul>
          <Paragraph weight="heavy">
            <Trans id="settings.organization.entities.groups.detail.delete.confirmation.description.cannot-be-undone">
              This action cannot be undone.
            </Trans>
          </Paragraph>
        </Stack>
      </ConfirmationDialog>
    </>
  );
};
